import 'firebase/compat/auth'

import { ActionContext, Module } from 'vuex'
import { AuthState, RootState } from './state_models'
import { getDecodedJWT, setJWT } from '../util/jwt_helper'

import { AuthUser } from '../models'
import { OauthService } from '@/services'
import Vue from 'vue'
import axios from 'axios'
import config from '../config'
import firebase from 'firebase/compat/app'
import localStorage from 'store'
import moment from 'moment'
import restAgentV1 from '../restAgent'
import router from '@/routes/index'
import  SocketsService  from '../services/SocketsService';

export const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state: {
    user: undefined,
    locationId: undefined as undefined | string,
    isUserSwitching: false,
    isTokenRefreshing: false
  },
  getters: {
    getAuth: state => {
      return state.user
    },
    isProxyUser: state => {
      return !!state?.user?.masterUser
    },
    isJwtExist: state => {
      return !!state.user?.jwt && !!state.user?.refreshJwt
    },
    isUserSwitching: state => {
      return state.isUserSwitching
    },
    isTokenRefreshing: state => {
      return state.isTokenRefreshing
    }
  },
  mutations: {
    setLocationId: (state: AuthState, locationId: string) => {
      state.locationId = locationId
    },
    set: (state: AuthState, payload: AuthUser) => {
      state.user = payload
    },
    delete: (state: AuthState) => {
      state.user = undefined
    },
    setUserSwitching: (state: AuthState, isSwitching: boolean) => {
      state.isUserSwitching = isSwitching
    },
    setTokenRefreshing: (state: AuthState, isRefreshing: boolean) => {
      state.isTokenRefreshing = isRefreshing
    }
  },
  actions: {
    refreshFirebaseToken: async (
      context: ActionContext<AuthState, RootState>,
      params: { [key: string]: any }
    ) => {
      //
      if (
        !params.locationId ||
        (context.state.locationId &&
          params.locationId === context.state.locationId && !params.refresh)
      ) {
        return
      }
      context.commit('setLocationId', params.locationId)
      const {
        activeLocations,
        isChanged
      } = await context.dispatch(
        'locations/addToActiveLocation',
        params.locationId && typeof params.locationId === 'string'
          ? [params.locationId]
          : params.locationId,
        { root: true }
      )
      if (!params.refresh && !isChanged) {
        return
      }
      try {
        const firebaseToken = await firebase?.auth()?.currentUser?.getIdToken() as string
        const decodedPayload = getDecodedJWT(firebaseToken)

        if(decodedPayload?.locations?.includes(params.locationId)) {
          console.log('skipping refresh token api call [reason: duplicate]')
          return
        }
      } catch (e) {
        console.error(e)
      }

      try {
        let currentRequestTimestamp = Date.now()
        const response = await axios.get(
          `${config.newBaseUrl}/signin/refresh?version=2&location_id=${activeLocations}`
        )
        // storing token refresh timestamp to handle race condition
        // older request should never overwrite the current token
        let lastRefreshTimestamp = localStorage.get('tokenRefreshTimestamp')
        const isInvalidRequest = lastRefreshTimestamp && lastRefreshTimestamp > currentRequestTimestamp
        if (response && response.status === 200 && !isInvalidRequest) {
          localStorage.set('tokenRefreshTimestamp', currentRequestTimestamp)
          await firebase.auth().signInWithCustomToken(response.data.token)
          localStorage.set('refreshedToken', response.data.token)
          restAgentV1.setApiKey(response.data.token)
        }
      } catch (e) {
        console.error(`Error in refreshing firebase token : ${JSON.stringify(e)}`)
      }
    },
    get: (context: ActionContext<AuthState, RootState>) => {
      return new Promise((resolve, reject) => {
        if (context.state.user) {
          return resolve(context.state.user)
        }
        const b64String = localStorage.get('a')
        //  'user_not_logged_in' is used in the sentry exception. Change it there too
        if (!b64String) return reject('The user is not logged in. user_not_logged_in')

        const user = JSON.parse(atob(b64String))
        context.commit('set', user)
        setJWT(user?.jwt, user?.authToken)
        return resolve(user)
      })
    },
    setTokenRefreshing: (context: ActionContext<AuthState, RootState>, isTokenRefreshing: boolean) => {
      context.commit('setTokenRefreshing', isTokenRefreshing)
    },
    set: (context: ActionContext<AuthState, RootState>, payload: AuthUser) => {
      context.commit('set', payload)
      localStorage.set('a', btoa(JSON.stringify(payload)))
      localStorage.set('loginDate', moment().toDate())
      const hostName = window.location.hostname.split('.')
      const hostLength = hostName.length
      let domainName = 'localhost'
      if (hostName[0] !== 'localhost') {
        domainName = `.${hostName[hostLength - 2]}.${hostName[hostLength - 1]}`
        if(hostName.includes("gohighlevel")){
          document.cookie = `a=${btoa(
            JSON.stringify(payload)
          )};domain=${domainName};path=/;max-Age=31536000;`
        }
      }
      setJWT(payload?.jwt, payload?.authToken)
    },
    switchToMasterUser: {
      handler: async (context: ActionContext<AuthState, RootState>) => {
        try {
            context.commit('setUserSwitching', true)
            context.commit('setAgencyLoaderState',true,{root: true})
            const authUser = await context.dispatch('auth/get', null, { root: true })
            const signedInUserData = authUser.masterUser || authUser

            setJWT(signedInUserData.jwt, signedInUserData.authToken, true)
            const data = await OauthService.getCurrentUser()
            const authSetData = {
                firebaseToken: data.token,
                jwt: data.jwt,
                refreshJwt: data.refreshJwt,
                apiKey: data.apiKey,
                userId: data.userId,
                companyId: data.companyId,
                authToken: data.authToken,
                refreshToken: data.refreshToken
            }

            /* Log out User*/
            await context.dispatch('logout', null, { root: true })
            await firebase.auth().signOut()

            /* switch back to logged user*/
            await context.dispatch('auth/set', authSetData, { root: true });
            await firebase.auth().signInWithCustomToken(authSetData.firebaseToken)
            await context.dispatch('auth/set', authSetData, { root: true });


            await context.dispatch('init', null, { root: true })
            await context.dispatch('user/get', null, { root: true })
            await context.dispatch('company/get', null, { root: true })

            await context.dispatch(
                'auth/refreshFirebaseToken',
                { locationId: router.currentRoute?.params?.location_id || null, refresh: true },
                { root: true }
            );
            localStorage.set('proxyLoginCount', (localStorage.get('proxyLoginCount') || 0)+1)
            window.location.reload()
        } catch (error) {
            console.error('something went wrong while swicthing', error)
            window.location.reload();
        }finally {
          context.commit('setAgencyLoaderState',false,{root: true})
        }
      }
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<AuthState, RootState>) => {
        localStorage.remove('app_referer')
        context.commit('delete')
        localStorage.remove('a')
        localStorage.remove('loginDate')
        try{
          await SocketsService.close()
        }catch(error){
          console.error('Socket Error', error)
        }
        if ( window.pendo) {
          window.pendo.clearSession();
        }
        document.cookie =
          'a=; Max-Age=-99999999;domain=.gohighlevel.com;path=/;'

        if (window.google && google.accounts) {
          google.accounts.id?.disableAutoSelect();
        }
      }
    },
    changeUserSwitching(context: ActionContext<AuthState, RootState>, isSwitching) {
      context.commit('setUserSwitching', isSwitching)
    },
    async signOutFromBackend(context: ActionContext<AuthState, RootState>) {
      try {
        if(localStorage.get('a')) {
          await axios.post(`${config.newBaseUrl}/signout`)
        } else {
          console.log('user already logged out')
        }
      } catch (error) {
        console.error(error)
      }
    }
  }
}
